import { createContext, useContext, useReducer, useEffect } from "react";
import { authApi, userApi, setToken } from "../services/api";

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  onboarding: null,
  preferences: null,
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_USER: "SET_USER",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  LOGOUT: "LOGOUT",
  SET_ONBOARDING: "SET_ONBOARDING",
  SET_PREFERENCES: "SET_PREFERENCES",
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: null,
      };
    case AUTH_ACTIONS.SET_USER: {
      console.log("🔍 AuthContext - SET_USER action payload:", action.payload);
      console.log(
        "🔍 AuthContext - SET_USER isAdmin:",
        action.payload?.isAdmin
      );
      const newState = {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      };
      console.log("🔍 AuthContext - New state after SET_USER:", newState);
      return newState;
    }
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
      };
    case AUTH_ACTIONS.SET_ONBOARDING: {
      console.log(
        "🔍 AuthContext - SET_ONBOARDING action payload:",
        action.payload
      );
      const onboardingState = {
        ...state,
        onboarding: action.payload,
      };
      console.log(
        "🔍 AuthContext - New state after SET_ONBOARDING:",
        onboardingState
      );
      return onboardingState;
    }
    case AUTH_ACTIONS.SET_PREFERENCES:
      return {
        ...state,
        preferences: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Only check if there's a token in localStorage
        const token = localStorage.getItem("readtheword_token");
        if (!token) {
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: null,
          });
          return;
        }

        const response = await authApi.getMe();
        console.log("🔍 AuthContext - /me response:", response.data);
        console.log("🔍 AuthContext - User data:", response.data.user);
        console.log(
          "🔍 AuthContext - User isAdmin:",
          response.data.user?.isAdmin
        );

        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: response.data.user,
        });

        // Load onboarding status and preferences
        try {
          const [onboardingRes, preferencesRes] = await Promise.all([
            userApi.getOnboardingStatus(),
            userApi.getPreferences(),
          ]);

          console.log(
            "🔍 Initial auth - Onboarding API response:",
            onboardingRes.data
          );
          console.log(
            "🔍 Initial auth - Preferences API response:",
            preferencesRes.data
          );

          dispatch({
            type: AUTH_ACTIONS.SET_ONBOARDING,
            payload: onboardingRes.data.onboarding,
          });

          dispatch({
            type: AUTH_ACTIONS.SET_PREFERENCES,
            payload: preferencesRes.data.preferences,
          });
        } catch (error) {
          console.error("Failed to load user data:", error);
          // Set default values on error
          dispatch({
            type: AUTH_ACTIONS.SET_ONBOARDING,
            payload: { currentStep: 1, completed: false, skippedSteps: [] },
          });

          dispatch({
            type: AUTH_ACTIONS.SET_PREFERENCES,
            payload: {
              bibleTranslation: "ESV",
              competitiveMode: false,
              appUsageMode: "tracking",
            },
          });
        }
      } catch {
        // If token is invalid, clear it
        setToken(null);
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: null,
        });
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await authApi.login(credentials);
      const { token, user } = response.data;

      setToken(token);
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });

      // Load onboarding status and preferences after login
      try {
        const [onboardingRes, preferencesRes] = await Promise.all([
          userApi.getOnboardingStatus(),
          userApi.getPreferences(),
        ]);

        dispatch({
          type: AUTH_ACTIONS.SET_ONBOARDING,
          payload: onboardingRes.data.onboarding,
        });

        dispatch({
          type: AUTH_ACTIONS.SET_PREFERENCES,
          payload: preferencesRes.data.preferences,
        });
      } catch (error) {
        console.error("Failed to load user data after login:", error);
        // Set default values on error
        dispatch({
          type: AUTH_ACTIONS.SET_ONBOARDING,
          payload: { currentStep: 1, completed: false, skippedSteps: [] },
        });

        dispatch({
          type: AUTH_ACTIONS.SET_PREFERENCES,
          payload: {
            bibleTranslation: "ESV",
            competitiveMode: false,
            appUsageMode: "tracking",
          },
        });
      }

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Sign in failed";
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await authApi.register(userData);
      const { token, user } = response.data;

      console.log("🔍 Registration response:", response.data);
      console.log("🔍 Setting token:", token);
      console.log("🔍 Setting user:", user);

      setToken(token);
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });

      // Load onboarding status and preferences after registration
      try {
        const [onboardingRes, preferencesRes] = await Promise.all([
          userApi.getOnboardingStatus(),
          userApi.getPreferences(),
        ]);

        console.log(
          "🔍 Registration - Onboarding API response:",
          onboardingRes.data
        );
        console.log(
          "🔍 Registration - Preferences API response:",
          preferencesRes.data
        );

        dispatch({
          type: AUTH_ACTIONS.SET_ONBOARDING,
          payload: onboardingRes.data.onboarding,
        });

        dispatch({
          type: AUTH_ACTIONS.SET_PREFERENCES,
          payload: preferencesRes.data.preferences,
        });
      } catch (error) {
        console.error("Failed to load user data after registration:", error);
        // Set default values on error
        dispatch({
          type: AUTH_ACTIONS.SET_ONBOARDING,
          payload: { currentStep: 1, completed: false, skippedSteps: [] },
        });

        dispatch({
          type: AUTH_ACTIONS.SET_PREFERENCES,
          payload: {
            bibleTranslation: "ESV",
            competitiveMode: false,
            appUsageMode: "tracking",
          },
        });
      }

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Registration failed";
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Update user profile
  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: { ...state.user, ...userData },
    });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await authApi.forgotPassword(email);
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Failed to send reset email";
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Reset password function
  const resetPassword = async (token, password) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await authApi.resetPassword(token, password);
      
      // If the response includes a token and user, automatically log them in
      if (response.data.token && response.data.user) {
        const { token: loginToken, user } = response.data;
        
        setToken(loginToken);
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: user,
        });

        // Load onboarding status and preferences after automatic login
        try {
          const [onboardingRes, preferencesRes] = await Promise.all([
            userApi.getOnboardingStatus(),
            userApi.getPreferences(),
          ]);

          dispatch({
            type: AUTH_ACTIONS.SET_ONBOARDING,
            payload: onboardingRes.data.onboarding,
          });

          dispatch({
            type: AUTH_ACTIONS.SET_PREFERENCES,
            payload: preferencesRes.data.preferences,
          });
        } catch (error) {
          console.error("Failed to load user data after password reset:", error);
          // Set default values on error
          dispatch({
            type: AUTH_ACTIONS.SET_ONBOARDING,
            payload: { currentStep: 1, completed: false, skippedSteps: [] },
          });

          dispatch({
            type: AUTH_ACTIONS.SET_PREFERENCES,
            payload: {
              bibleTranslation: "ESV",
              competitiveMode: false,
              appUsageMode: "tracking",
            },
          });
        }
      }
      
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Failed to reset password";
      dispatch({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Onboarding functions
  const loadOnboardingStatus = async () => {
    try {
      const response = await userApi.getOnboardingStatus();
      dispatch({
        type: AUTH_ACTIONS.SET_ONBOARDING,
        payload: response.data.onboarding,
      });
      return response.data.onboarding;
    } catch (error) {
      console.error("Failed to load onboarding status:", error);
      throw error;
    }
  };

  const advanceOnboarding = async (skip = false) => {
    try {
      const response = await userApi.advanceOnboarding(skip);
      dispatch({
        type: AUTH_ACTIONS.SET_ONBOARDING,
        payload: response.data.onboarding,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to advance onboarding:", error);
      throw error;
    }
  };

  const completeOnboarding = async () => {
    try {
      const response = await userApi.completeOnboarding();
      dispatch({
        type: AUTH_ACTIONS.SET_ONBOARDING,
        payload: response.data.onboarding,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to complete onboarding:", error);
      throw error;
    }
  };

  // Preferences functions
  const loadPreferences = async () => {
    try {
      const response = await userApi.getPreferences();
      dispatch({
        type: AUTH_ACTIONS.SET_PREFERENCES,
        payload: response.data.preferences,
      });
      return response.data.preferences;
    } catch (error) {
      console.error("Failed to load preferences:", error);
      throw error;
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      const response = await userApi.updatePreferences(preferences);
      dispatch({
        type: AUTH_ACTIONS.SET_PREFERENCES,
        payload: response.data.preferences,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to update preferences:", error);
      throw error;
    }
  };

  // Context value
  const value = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    clearError,
    forgotPassword,
    resetPassword,
    // Onboarding
    loadOnboardingStatus,
    advanceOnboarding,
    completeOnboarding,
    // Preferences
    loadPreferences,
    updatePreferences,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
